import React, { useEffect, useState } from 'react';
import { Modal, Box, Grid } from '@mui/material';
import { keyframes } from '@mui/system';

import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import FlightIcon from '@mui/icons-material/Flight';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';

import { BadgePropsType } from './Type';

/* Animations */
const fadeScale = keyframes`
  0% { opacity: 0; transform: scale(0.9); }
  100% { opacity: 1; transform: scale(1); }
`;

const truckDrive = keyframes`
  0%, 100% { transform: translateX(-8px); }
  50% { transform: translateX(8px); }
`;

const roadMove = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-40px); }
`;

const speedLines = keyframes`
  0% { transform: translateX(0) scaleX(0); opacity: 0; }
  50% { opacity: 0.6; }
  100% { transform: translateX(-40px) scaleX(1); opacity: 0; }
`;

const dotPulse = keyframes`
  0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1.2); }
`;

export function Loader(props: BadgePropsType): JSX.Element {
  const { children, overly, loading } = props;

  /* 🚀 ICON ROTATION LOGIC */
  const icons = [LocalShippingIcon, FlightIcon, DeliveryDiningIcon, DirectionsBoatIcon];

  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex(prev => (prev + 1) % icons.length);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const CurrentIcon = icons[index];

  return (
    <>
      <Modal
        open={loading}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(6px)',
          backgroundColor: 'rgba(0, 0, 0, 0.35)',
        }}
      >
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          sx={{
            height: '100%',
            animation: `${fadeScale} 0.25s ease-out`,
          }}
        >
          <Box
            sx={{
              width: 120, // ⬇ decreased from 220
              padding: '10px', // ⬇ decreased from 32px
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2, // ⬇ decreased gap
              borderRadius: '20px',
            }}
          >
            {/* SPEED + ICON */}
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: 46, // ⬇ decreased from 60
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {/* Speed Lines */}
              {[0, 0.3, 0.6].map((delay, idx) => (
                <Box
                  key={idx}
                  sx={{
                    position: 'absolute',
                    right: '45%',
                    width: 42, // ⬇ decreased from 60
                    height: 2,
                    background: theme =>
                      `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
                    animation: `${speedLines} 1.2s ease-in-out infinite`,
                    animationDelay: `${delay}s`,
                    opacity: 0.4,
                    top: `${40 + idx * 12}%`,
                  }}
                />
              ))}

              {/* 🔁 Dynamic Icon */}
              <Box
                sx={{
                  position: 'relative',
                  animation: `${truckDrive} 1.5s ease-in-out infinite`,
                }}
              >
                <CurrentIcon
                  key={index}
                  sx={{
                    fontSize: 42, // ⬇ decreased from 56
                    color: 'primary.main',
                    animation: `${fadeScale} 0.4s ease`,
                    filter: theme => `drop-shadow(0 4px 10px ${theme.palette.primary.main}40)`,
                  }}
                />
              </Box>
            </Box>

            {/* ROAD */}
            <Box
              sx={{
                width: '100%',
                height: 3, // ⬇ decreased from 4
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 2,
                mt: '-22px', // ⬇ adjusted
                background: theme => `${theme.palette.primary.main}15`,
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  height: '100%',
                  width: '200%',
                  backgroundImage: theme =>
                    `repeating-linear-gradient(
            90deg,
            ${theme.palette.primary.main} 0px,
            ${theme.palette.primary.main} 16px,
            transparent 16px,
            transparent 32px
          )`,
                  animation: `${roadMove} 1s linear infinite`,
                }}
              />
            </Box>

            {/* DOTS */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {[0, 0.2, 0.4].map((delay, idx) => (
                <Box
                  key={idx}
                  sx={{
                    width: 6, // ⬇ decreased from 8
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    animation: `${dotPulse} 1.4s ease-in-out infinite`,
                    animationDelay: `${delay}s`,
                  }}
                />
              ))}
            </Box>
          </Box>
        </Grid>
      </Modal>

      {overly && children}
    </>
  );
}

export default Loader;
